import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useAppContext } from "../../../../hooks/useAppContext";
import { useClient } from "../../../../hooks/useClient";
import { useDefinition } from "../../../../hooks/useDefinition";
import { useSelectedSpread } from "../../../../hooks/useSelection";

export function useBackgroundPatterns() {
	const { reactQueryNoReloadOptions } = useAppContext();
	const definition = useDefinition();
	const currentSpread = useSelectedSpread();
	const client = useClient();

	const loadPatternsAsync = useCallback(() => {
		if (!definition || !currentSpread) return [];

		const { designId, assets, product, defaultColorEffect } = definition;
		const { designTypeId } = product;

		return client
			.loadAllBackgroundsAsync(designId, designTypeId, defaultColorEffect, assets)
			.then((res) =>
				res.filter(
					(background): background is eXpress.core.StockBackground =>
						background instanceof eXpress.core.StockBackground,
				),
			);
	}, [client, definition, currentSpread]);

	const { data: allBackgrounds } = useQuery<eXpress.core.StockBackground[]>({
		queryKey: ["useBackgroundPatterns", definition?.designId, definition?.product.designTypeId],
		queryFn: loadPatternsAsync,
		...reactQueryNoReloadOptions,
	});

	const backgroundsForCurrentSpread = useMemo(() => {
		if (!allBackgrounds || allBackgrounds.length === 0 || currentSpread?.kind === undefined)
			return [];
		return allBackgrounds.filter((bg) => bg.supportedSpreadKinds.has(currentSpread.kind));
	}, [allBackgrounds, currentSpread]);

	return backgroundsForCurrentSpread;
}
